/**
 * The proctoring client, as a hook.
 *
 * v1 POLICY — LOG ONLY. Nothing in here auto-submits, kicks the student out,
 * blanks the paper, or blocks input (beyond cancelling copy/paste, which is a
 * deliberate exam rule rather than a punishment). A false positive during a
 * 3-hour exam must cost the student nothing but a banner. The admin reviews the
 * event log afterwards and decides. Do not "upgrade" this to enforcement
 * without a conversation.
 *
 * Detected:
 *   TAB_SWITCH      document.visibilityState -> hidden
 *   WINDOW_BLUR     window blur (alt-tab to a window on the same screen)
 *   FULLSCREEN_EXIT fullscreenchange -> not fullscreen
 *   COPY_ATTEMPT    copy/cut  (prevented)
 *   PASTE_ATTEMPT   paste     (prevented)
 *   WEBCAM_STOPPED  a video track ends or is muted
 *   NETWORK_DROP    window offline
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ProctorEventType } from "@exam/shared";
import type { ServerClock } from "@/lib/exam/serverClock";
import { ProctorQueue, type ProctorQueueState } from "./queue";
import {
  enterFullscreen,
  isFullscreen,
  onFullscreenChange,
} from "./fullscreen";

export interface UseProctorOptions {
  attemptId: string;
  /** Nothing is monitored until this is true (i.e. once the exam starts). */
  active: boolean;
  /** Server-anchored clock — the source of every event timestamp. */
  clock: ServerClock;
  /** `attempts.started_at`, the master timeline origin. */
  startedAtMs: number | null;
  /** Webcam stream to watch for track-ended / muted. */
  stream?: MediaStream | null;
  /** Fullscreen is required for the exam runner, not for setup. */
  requireFullscreen?: boolean;
  /** Set on the phone only. */
  deviceToken?: string;
}

export interface ProctorHandle {
  state: ProctorQueueState;
  /** False while the student is out of fullscreen and it is required. */
  fullscreenActive: boolean;
  online: boolean;
  /** Manually log something the UI noticed (e.g. the phone dropped). */
  record: (type: ProctorEventType, metadata?: Record<string, unknown>) => void;
  /** Call from a click handler to (re-)enter fullscreen. */
  requestFullscreen: () => Promise<boolean>;
  /** Flush and stop — call before navigating away after submit. */
  finish: () => Promise<void>;
}

const EMPTY_STATE: ProctorQueueState = {
  counts: {},
  totalViolations: 0,
  pending: 0,
  degraded: false,
  lastEvent: null,
};

export function useProctor(options: UseProctorOptions): ProctorHandle {
  const {
    attemptId,
    active,
    clock,
    startedAtMs,
    stream,
    requireFullscreen = false,
    deviceToken,
  } = options;

  const queueRef = useRef<ProctorQueue | null>(null);
  const [state, setState] = useState<ProctorQueueState>(EMPTY_STATE);
  const [fullscreenActive, setFullscreenActive] = useState(isFullscreen);
  const [online, setOnline] = useState(() => navigator.onLine);

  /* -- queue lifecycle ---------------------------------------------- */

  // The queue is created once per attempt and deliberately NOT re-created when
  // `startedAtMs` arrives — tearing it down would drop queued events. The new
  // origin is pushed into the live instance instead.
  useEffect(() => {
    if (!active) return;

    const queue = new ProctorQueue({
      attemptId,
      clock,
      startedAtMs,
      deviceToken,
      onChange: setState,
    });
    queueRef.current = queue;
    setState(queue.state);

    return () => {
      queueRef.current = null;
      void queue.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [attemptId, active, clock, deviceToken]);

  useEffect(() => {
    queueRef.current?.setStartedAt(startedAtMs);
  }, [startedAtMs]);

  const record = useCallback(
    (type: ProctorEventType, metadata?: Record<string, unknown>) => {
      queueRef.current?.record(type, metadata);
    },
    [],
  );

  /* -- tab switch / window blur ------------------------------------- */

  useEffect(() => {
    if (!active) return;

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        record("TAB_SWITCH", { at: "visibilitychange" });
      }
    };

    // A blur that coincides with the tab going hidden is the same physical
    // action; only report the blur when the document is still visible.
    const onBlur = () => {
      if (document.visibilityState === "visible") {
        record("WINDOW_BLUR", { at: "window.blur" });
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
  }, [active, record]);

  /* -- fullscreen ---------------------------------------------------- */

  useEffect(() => {
    if (!active) return;

    setFullscreenActive(isFullscreen());

    return onFullscreenChange((fs) => {
      setFullscreenActive(fs);
      if (!fs && requireFullscreen) {
        record("FULLSCREEN_EXIT", { at: "fullscreenchange" });
      }
    });
  }, [active, requireFullscreen, record]);

  const requestFullscreen = useCallback(async () => {
    const ok = await enterFullscreen();
    setFullscreenActive(isFullscreen());
    return ok;
  }, []);

  /* -- copy / paste (blocked) ---------------------------------------- */

  useEffect(() => {
    if (!active) return;

    const onCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      record("COPY_ATTEMPT", { kind: e.type });
    };
    const onPaste = (e: ClipboardEvent) => {
      e.preventDefault();
      record("PASTE_ATTEMPT", { kind: e.type });
    };
    const onContextMenu = (e: MouseEvent) => e.preventDefault();

    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("contextmenu", onContextMenu);
    return () => {
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("contextmenu", onContextMenu);
    };
  }, [active, record]);

  /* -- webcam health -------------------------------------------------- */

  useEffect(() => {
    if (!active || !stream) return;

    const tracks = stream.getVideoTracks();
    const onEnded = () => record("WEBCAM_STOPPED", { reason: "track ended" });
    const onMute = () => record("WEBCAM_STOPPED", { reason: "track muted" });

    for (const t of tracks) {
      t.addEventListener("ended", onEnded);
      t.addEventListener("mute", onMute);
    }

    // A track can also be stopped without firing "ended" (device unplugged on
    // some platforms), so poll readyState as a backstop.
    const poll = setInterval(() => {
      if (tracks.some((t) => t.readyState === "ended")) {
        record("WEBCAM_STOPPED", { reason: "readyState ended" });
      }
    }, 10_000);

    return () => {
      clearInterval(poll);
      for (const t of tracks) {
        t.removeEventListener("ended", onEnded);
        t.removeEventListener("mute", onMute);
      }
    };
  }, [active, stream, record]);

  /* -- network ------------------------------------------------------- */

  useEffect(() => {
    if (!active) return;

    const goOffline = () => {
      setOnline(false);
      record("NETWORK_DROP", { at: "offline" });
    };
    const goOnline = () => setOnline(true);

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, [active, record]);

  /* -- unload -------------------------------------------------------- */

  useEffect(() => {
    if (!active) return;

    const onPageHide = () => queueRef.current?.flushOnUnload();
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      queueRef.current?.flushOnUnload();
      e.preventDefault();
    };

    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [active]);

  const finish = useCallback(async () => {
    await queueRef.current?.stop();
  }, []);

  return useMemo(
    () => ({
      state,
      fullscreenActive,
      online,
      record,
      requestFullscreen,
      finish,
    }),
    [state, fullscreenActive, online, record, requestFullscreen, finish],
  );
}
